﻿import React, { useEffect, useState } from "react";
import { Box, IconButton } from "@mui/material";
import {
  ShepDataGridPro,
  ShepCheckbox,
  ShepTooltip,
  ShepPagination,
} from "@/components/GenericComponents/ShepComponents";
import { useDispatch, useSelector } from "react-redux";
import {
  selectConnectionQualifiers,
  selectConnectionQualifiersLoaded,
  selectCatalogSummaryRecords,
  selectCatalogSummaryRecordsLoaded,
  fetchCatalogSummaries,
  selectCatalogSummaryRecordCount,
  selectCatalogSummarySearchText,
  selectCatalogSummarySelectedTab,
  selectAllCatalogCustomFields,
  fetchCatalogCustomFields,
} from "@/features/maintenance/maintenanceSlice";
import {
  selectConnectionNamesRecords,
  selectSelectedConnection,
} from "@/features/connections/integrationSlice";
import CatalogGridToolbar from "./CatalogGridToolbar";
import {
  setConnectionName,
  resetCatalogData,
  setCatalogData,
  setCatalogIdToEdit,
  setCatalogType,
  setIsAddCatalogOpen,
  resetAddEditPopUp,
} from "@/features/connections/manageCatalogDataSlice";
import { CatalogTypes } from "./MaintenanceTypes";
import { EditIcon } from "../../ImageRepo";
import { CatalogPopup } from "./AddCatalogs/CatalogPopup";
import { setCatalogDataField } from "../../features/connections/manageCatalogDataSlice";

const CatalogGrid = (props) => {
  const dispatch = useDispatch();

  const selectedConnection = useSelector(selectSelectedConnection);
  const connectionQualifiers = useSelector(selectConnectionQualifiers);
  const connectionQualifiersLoaded = useSelector(
    selectConnectionQualifiersLoaded,
  );
  const allConnections = useSelector(selectConnectionNamesRecords);
  const recordLoaded = useSelector(selectCatalogSummaryRecordsLoaded);
  const rows = useSelector(selectCatalogSummaryRecords);
  const rowCount = useSelector(selectCatalogSummaryRecordCount);
  const searchText = useSelector(selectCatalogSummarySearchText);
  const currentCatalog = useSelector(selectCatalogSummarySelectedTab);
  const [additionalColumns, setAdditionalColumns] = useState([]);
  const allCustomFields = useSelector(selectAllCatalogCustomFields);

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 15,
    page: 0,
  });
  const onPaginationModelChange = async (newPaginationModel) => {
    dispatch(
      fetchCatalogSummaries({ ...newPaginationModel, search: searchText }),
    );
    setPaginationModel(newPaginationModel);
  };

  const definedRowSlice = rows
    .slice(
      paginationModel.page * paginationModel.pageSize,
      (paginationModel.page + 1) * paginationModel.pageSize,
    )
    .filter((row) => row); // Ensure all visible rows are defined to prevent errors

  const getConnectionName = (id) => {
    if (id && allConnections && allConnections.length) {
      const c = allConnections.filter((x) => x.Id === id)[0];
      if (c) {
        dispatch(
          setConnectionName({
            connectionName: c.Name,
            displayName: c.DisplayName,
          }),
        );
        return c.DisplayName;
      }
    }
    dispatch(setConnectionName({ connectionName: "", displayName: "" }));
    return "";
  };

  useEffect(() => {
    dispatch(resetAddEditPopUp());
    dispatch(fetchCatalogCustomFields());
  }, []);

  useEffect(() => {
    const customFieldColumns = [
      ...(allCustomFields[currentCatalog]?.map((field) => {
        return {
          field: `CustomField_${field.FieldId}`,
          headerName: field.Code,
          width: 220,
          renderCell: (params) => {
            const catalogData = JSON.parse(params.row.CatalogData);
            return (
              catalogData.CustomFields?.find(
                (rq) => rq.CatalogCustomFieldId === field.FieldId,
              )?.FieldValue ?? ""
            );
          },
        };
      }) ?? []),
      ...(allCustomFields[-1]?.map((field) => {
        return {
          field: `CustomField_${field.FieldId}`,
          headerName: field.Code,
          width: 220,
          renderCell: (params) => {
            const catalogData = JSON.parse(params.row.CatalogData);
            return (
              catalogData.CustomFields?.find(
                (rq) => rq.CatalogCustomFieldId === field.FieldId,
              )?.FieldValue ?? ""
            );
          },
        };
      }) ?? []),
    ];
    if (currentCatalog === CatalogTypes.Item) {
      const qualifierColumns = connectionQualifiers.map((qualifier) => ({
        field: qualifier,
        headerName: qualifier,
        width: 220,
        renderCell: (params) =>
          params.row.Qualifiers.find((rq) => rq.Name === qualifier)?.Value ??
          "",
      }));
      setAdditionalColumns([...qualifierColumns, ...customFieldColumns]);
    } else {
      setAdditionalColumns(customFieldColumns);
    }
  }, [currentCatalog, connectionQualifiers, allCustomFields]);

  const handleEdit = async (params) => {
    const currentCon = allConnections.filter(
      (x) => x.ConnectionId == selectedConnection[0],
    )[0];
    const ItemQualifiers = params.row.Qualifiers?.reduce((acc, q) => {
      acc[q.Name] = q.Value;
      return acc;
    }, {});
    await dispatch(setCatalogType({ catalogType: currentCatalog }));
    await dispatch(
      setCatalogData({
        data: {
          ...JSON.parse(params.row.CatalogData),
          ItemQualifiers: ItemQualifiers ?? {},
        },
      }),
    );
    //temporary field needed for editing items, ItemQualifiers get overwritten & causes recursive render
    await dispatch(
      setCatalogDataField({
        name: "ExistingQualifiers",
        value: ItemQualifiers,
      }),
    );
    await dispatch(setCatalogIdToEdit({ catalogId: params.row.CatalogId }));
    dispatch(
      setIsAddCatalogOpen({
        isOpen: true,
        isEdit: true,
        connectionInfo: {
          displayName: currentCon.DisplayName,
          connectionId: currentCon.ConnectionId,
        },
        catalogType: currentCatalog,
      }),
    );
  };

  const columns = [
    {
      field: "ConnectionId",
      headerName: "Connection",
      width: 230,
      renderCell: (params) => {
        return getConnectionName(params.value);
      },
    },
    {
      field: "Reference",
      headerName: "Reference ID / Key",
      width: 300,
    },
    ...additionalColumns,
    {
      field: "edit",
      headerName: "edit",
      width: 120,
      sortable: false,
      filterable: false,
      resizable: false,
      renderCell: (params) => (
        <>
          <IconButton onClick={() => handleEdit(params)}>
            <EditIcon />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ display: "block", flexDirection: "column", width: "100%" }}>
      <Box
        className="content-box"
        sx={{ display: "flex", flexDirection: "row" }}
      >
        <Box sx={{ width: "100%" }}>
          <Box sx={{ width: "100%", maxWidth: "100%", overflowX: "auto" }}>
            <ShepDataGridPro
              isDataLoaded={recordLoaded && connectionQualifiersLoaded}
              rows={definedRowSlice}
              rowCount={rowCount}
              columns={columns}
              columnHeaderHeight={78}
              rowHeight={100}
              pagination
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={onPaginationModelChange}
              getRowId={(row) => row.CatalogId}
              whiteHeader={true}
              checkboxSelection={true}
              disableMultipleRowSelection={false}
              disableColumnMenu={true}
              disableColumnFilter={true}
              disableColumnReorder={true}
              hideFooterSelectedRowCount={true}
              disableRowSelectionOnClick={!props.canSelect}
              initialState={{
                pinnedColumns: {
                  right: ["edit"],
                },
              }}
              slots={{
                baseCheckbox: ShepCheckbox,
                baseTooltip: ShepTooltip,
                toolbar: CatalogGridToolbar,
                pagination: ShepPagination,
              }}
              sx={{
                color: "var(--text-primary)",
                fontSize: "var(--font-sm)",
                fontFamily: "var(--font-family)",
                borderTopLeftRadius: "12px",
                borderTopRightRadius: "12px",
                backgroundColor: "var(--default-light)",
                border: "none",
                "& .MuiDataGrid:hover": {
                  backgroundColor: "inherit",
                },
                "& .MuiDataGrid-cellCheckbox, & .workflow-row": {
                  opacity: 0,
                  borderBottom: "none",
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "none",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "var(--tertiary-13)",
                },
                "& .Mui-selected": {
                  backgroundColor: "var(--primary-pale)",
                },
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader.MuiDataGrid-columnHeaderCheckbox":
                  {
                    outline: "none",
                  },
                "& .MuiDataGrid-row:hover .MuiDataGrid-cellCheckbox, & .MuiDataGrid-row:hover .workflow-row":
                  {
                    opacity: 1,
                  },
                "& .MuiDataGrid-cellCheckbox": {
                  borderLeft: "8px solid transparent",
                },
                "& .Mui-selected .MuiDataGrid-cellCheckbox": {
                  opacity: 1,
                  borderLeft: "8px solid var(--primary-main)",
                  borderTopLeftRadius: "4px",
                  borderBottomLeftRadius: "4px",
                },
                "& .MuiDataGrid-columnHeaderCheckbox": {
                  outline: "none",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "var(--font-md)",
                  fontWeight: "var(--font-bold)",
                  borderLeft: "none",
                },
                "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:hover":
                  {
                    borderBottomLeftRadius: "4px",
                    borderBottomRightRadius: "4px",
                    backgroundColor: "var(--primary-pale)",
                    borderBottom: "none",
                    borderLeft: "none",
                  },
                "& .MuiDataGrid-columnHeader[data-field='connection']": {
                  marginLeft: props.canSelect ? 0 : "50px",
                },
                "& .MuiDataGrid-pinnedColumnHeaders.MuiDataGrid-pinnedColumnHeaders--right":
                  {
                    display: "none",
                  },
                "& .MuiDataGrid-cell[data-field='connection']": {
                  marginLeft: props.canSelect ? 0 : "50px",
                },
                ...(selectedConnection &&
                Object.keys(selectedConnection).length === 0
                  ? {
                      "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-footerContainer, & .MuiDataGrid-scrollbar, & .MuiDataGrid-virtualScroller, & .gridHeaderSegment":
                        { display: "none" },
                      background: "transparent",
                      minHeight: "600px",
                    }
                  : {}),
              }}
            />
          </Box>
        </Box>
      </Box>
      {currentCatalog !== -1 && (
        <CatalogPopup
          onClose={() => {}}
          onComplete={() => onPaginationModelChange(paginationModel)}
        />
      )}
    </Box>
  );
};

export default CatalogGrid;
